import React, { useState, useEffect } from 'react';
import { liveStreamsAPI, matchesAPI } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import { FaPlus, FaTrash, FaYoutube, FaEye, FaExclamationCircle } from 'react-icons/fa';

const LiveManagementSection = () => {
  const { language } = useLanguage();
  const [liveStreams, setLiveStreams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Formulaire d'ajout
  const [formData, setFormData] = useState({
    title: '',
    title_ar: '',
    youtube_url: '',
    match_id: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [streamsRes, matchesRes] = await Promise.all([
        liveStreamsAPI.getAll().catch(() => ({ data: [] })),
        matchesAPI.getAll().catch(() => ({ data: [] }))
      ]);
      setLiveStreams(Array.isArray(streamsRes.data) ? streamsRes.data : []);
      setMatches(Array.isArray(matchesRes.data) ? matchesRes.data : []);
    } catch (error) {
      console.error('Erreur de chargement:', error);
    }
  };

  const handleCreateLive = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.youtube_url) {
      alert(language === 'ar' ? 'الرجاء إدخال عنوان البث ورابط يوتيوب المباشر' : 'Veuillez saisir le titre et le lien YouTube');
      return;
    }

    setSubmitting(true);
    try {
      await liveStreamsAPI.create({
        title: formData.title,
        title_ar: formData.title_ar || formData.title,
        youtube_url: formData.youtube_url,
        match_id: formData.match_id ? parseInt(formData.match_id) : null
      });

      alert(language === 'ar' ? 'تمت إضافة البث المباشر بنجاح!' : 'Bât مباشر ajouté avec succès!');
      setFormData({ title: '', title_ar: '', youtube_url: '', match_id: '' });
      await loadData();
    } catch (error) {
      console.error('Erreur d\'ajout:', error);
      alert(language === 'ar' ? 'حدث خطأ أثناء إضافة البث' : 'Erreur lors de l\'ajout du live');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteLive = async (id) => {
    if (!window.confirm(language === 'ar' ? 'هل أنت تأكد من حذف/إيقاف هذا البث؟' : 'Voulez-vous supprimer ce flux direct ?')) return;

    try {
      await liveStreamsAPI.delete(id);
      await loadData();
    } catch (error) {
      console.error('Erreur de suppression:', error);
    }
  };

  const handleMatchStatusChange = async (match, newStatus) => {
    try {
      await matchesAPI.update(match.id, { ...match, status: newStatus });
      await loadData();
    } catch (error) {
      console.error('Erreur mise à jour statut:', error);
    }
  };

  const handleMatchUrlBlur = async (match, newUrl) => {
    if (newUrl === (match.youtube_url || '')) return;
    try {
      await matchesAPI.update(match.id, { ...match, youtube_url: newUrl });
      await loadData();
    } catch (error) {
      console.error('Erreur mise à jour URL:', error);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return language === 'ar' ? 'تاريخ غير محدد' : 'Date non définie';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString(language === 'ar' ? 'ar-TN' : 'fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateStr;
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.pageTitle}>
            {language === 'ar' ? 'فضاء البث المباشر (إدارة البث)' : 'Espace Direct Live'}
          </h1>
          <p style={styles.subTitle}>
            {language === 'ar' ? 'أضف بث مباشر جديد عبر رابط يوتيوب والتحكم في البث المباشر للمباريات' : 'Ajoutez un flux direct YouTube et gérez les diffusions en direct'}
          </p>
        </div>

        <a
          href="/live"
          target="_blank"
          rel="noreferrer"
          style={styles.previewBtn}
        >
          <FaEye /> {language === 'ar' ? 'معاينة صفحة البث المباشر' : 'Voir la page Live (Public)'}
        </a>
      </div>

      {/* Formulaire : أضف بث مباشر */}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div style={styles.badgeLive}>🔴 {language === 'ar' ? 'أضف بث مباشر جديد' : 'Ajouter un nouveau Live'}</div>
        </div>

        <form onSubmit={handleCreateLive} style={styles.form}>
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                {language === 'ar' ? 'عنوان البث المباشر *' : 'Titre du live *'}
              </label>
              <input
                type="text"
                required
                placeholder={language === 'ar' ? 'مثال: بث مباشر: النادي الصفاقسي vs الملعب التونسي' : 'ex: Live Match CSS vs ST'}
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                style={styles.input}
              />
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                {language === 'ar' ? 'رابط يوتيوب المباشر *' : 'Lien direct YouTube *'}
              </label>
              <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                <FaYoutube style={{ position: 'absolute', right: '12px', color: '#ff0000', fontSize: '20px' }} />
                <input
                  type="text"
                  required
                  placeholder="https://www.youtube.com/watch?v=... ou https://youtu.be/..."
                  value={formData.youtube_url}
                  onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                  style={{ ...styles.input, paddingRight: '42px' }}
                />
              </div>
            </div>
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>
                {language === 'ar' ? 'ربط بمباراة (اختياري)' : 'Lier à un match (Optionnel)'}
              </label>
              <select
                value={formData.match_id}
                onChange={(e) => setFormData({ ...formData, match_id: e.target.value })}
                style={styles.select}
              >
                <option value="">-- {language === 'ar' ? 'بدون مباراة (بث مستقل)' : 'Sans match (Flux indépendant)'} --</option>
                {matches.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.home_team_name} vs {m.away_team_name} ({formatDate(m.match_date)})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ ...styles.formGroup, justifyContent: 'flex-end' }}>
              <button
                type="submit"
                disabled={submitting}
                style={styles.submitBtn}
              >
                <FaPlus /> {submitting ? (language === 'ar' ? 'جاري الإضافة...' : 'Ajout...') : (language === 'ar' ? '🔴 إضافة وإطلاق البث المباشر' : '🔴 Lancer le direct')}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Liste des Flux Directs Actifs */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>
          🔴 {language === 'ar' ? 'قائمة البث المباشر الحالي' : 'Flux en direct actuels'} ({liveStreams.length})
        </h2>

        {liveStreams.length > 0 ? (
          <div style={styles.streamsGrid}>
            {liveStreams.map((stream) => (
              <div key={stream.id} style={styles.streamItem}>
                <div style={styles.streamInfo}>
                  <div style={styles.streamHeader}>
                    <span style={styles.badgeActive}>🔴 {language === 'ar' ? 'مباشر الآن' : 'EN DIRECT'}</span>
                    <h3 style={styles.streamTitleText}>{stream.title || stream.title_ar}</h3>
                  </div>

                  <div style={styles.streamUrlText}>
                    <FaYoutube color="#ff0000" />
                    <span style={{ wordBreak: 'break-all' }}>{stream.youtube_url}</span>
                  </div>

                  {stream.home_team_name && stream.away_team_name && (
                    <div style={styles.matchTeamsBadge}>
                      ⚽ {stream.home_team_name} vs {stream.away_team_name}
                    </div>
                  )}
                </div>

                <button
                  onClick={() => handleDeleteLive(stream.id)}
                  style={styles.deleteStreamBtn}
                  title={language === 'ar' ? 'إيقاف/حذف البث' : 'Arrêter le live'}
                >
                  <FaTrash /> {language === 'ar' ? 'حذف/إيقاف' : 'Supprimer'}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div style={styles.emptyState}>
            <FaExclamationCircle size={40} color="#ccc" />
            <p>{language === 'ar' ? 'لا يوجد بث مباشر نشط حالياً. استخدم النموذج أعلاه لإضافة بث مباشر.' : 'Aucun flux direct actif pour le moment.'}</p>
          </div>
        )}
      </div>

      {/* Contrôle des matchs */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>
          ⚽ {language === 'ar' ? 'التحكم في حالة بث المباريات' : 'Gestion des flux par match'}
        </h2>

        <div style={{ display: 'grid', gap: '15px' }}>
          {(matches || [])
            .slice(0, 15)
            .map((match) => (
              <div
                key={match.id}
                style={{
                  ...styles.matchRow,
                  background: match.status === 'live' ? '#fff5f5' : '#fff',
                  borderLeft: match.status === 'live' ? '5px solid #ff0000' : '5px solid #e0e0e0'
                }}
              >
                <div style={styles.matchInfoCol}>
                  <div style={styles.matchTeamsNames}>
                    {match.home_team_name} vs {match.away_team_name}
                  </div>
                  <div style={styles.matchDateText}>
                    📅 {formatDate(match.match_date)}
                  </div>
                </div>

                <div style={styles.matchStatusCol}>
                  <select
                    value={match.status || 'upcoming'}
                    onChange={(e) => handleMatchStatusChange(match, e.target.value)}
                    style={{
                      ...styles.selectStatus,
                      background: match.status === 'live' ? '#ffebee' : '#fff'
                    }}
                  >
                    <option value="upcoming">{language === 'ar' ? 'قادمة (À venir)' : 'À venir'}</option>
                    <option value="live">{language === 'ar' ? '🔴 مباشر (En direct)' : '🔴 En direct'}</option>
                    <option value="finished">{language === 'ar' ? 'منتهية (Terminé)' : 'Terminé'}</option>
                  </select>
                </div>

                <div style={styles.matchUrlCol}>
                  <FaYoutube color="#ff0000" size={20} />
                  <input
                    type="text"
                    placeholder={language === 'ar' ? 'رابط (ID) يوتيوب المباشر' : 'Lien YouTube direct'}
                    defaultValue={match.youtube_url || ''}
                    onBlur={(e) => handleMatchUrlBlur(match, e.target.value)}
                    style={styles.matchUrlInput}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
};

const styles = {
  container: {
    padding: '10px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '24px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '15px'
  },
  pageTitle: {
    fontSize: '26px',
    fontWeight: 'bold',
    margin: 0,
    color: '#111'
  },
  subTitle: {
    fontSize: '14px',
    color: '#666',
    margin: '4px 0 0 0'
  },
  previewBtn: {
    background: '#1a1a1a',
    color: '#fff',
    padding: '10px 20px',
    borderRadius: '10px',
    textDecoration: 'none',
    fontWeight: 'bold',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
  },
  card: {
    background: '#ffffff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
    border: '1px solid #eaeaea'
  },
  cardHeader: {
    marginBottom: '20px'
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 'bold',
    margin: '0 0 20px 0',
    color: '#222',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  badgeLive: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    background: '#ffebee',
    color: '#d32f2f',
    padding: '6px 14px',
    borderRadius: '20px',
    fontWeight: 'bold',
    fontSize: '14px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  formRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
    gap: '20px'
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333'
  },
  input: {
    padding: '12px 15px',
    borderRadius: '10px',
    border: '1px solid #ccc',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box'
  },
  select: {
    padding: '12px 15px',
    borderRadius: '10px',
    border: '1px solid #ccc',
    fontSize: '14px',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
    background: '#fff'
  },
  submitBtn: {
    background: 'linear-gradient(135deg, #d32f2f, #b71c1c)',
    color: '#fff',
    border: 'none',
    padding: '12px 24px',
    borderRadius: '10px',
    fontSize: '15px',
    fontWeight: 'bold',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    boxShadow: '0 4px 15px rgba(211, 47, 47, 0.3)',
    height: '46px'
  },
  streamsGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '15px'
  },
  streamItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    padding: '16px 20px',
    borderRadius: '12px',
    background: '#fff8f8',
    border: '1px solid #ffcdd2',
    gap: '15px',
    flexWrap: 'wrap'
  },
  streamInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    flex: 1
  },
  streamHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px'
  },
  badgeActive: {
    background: '#d32f2f',
    color: '#fff',
    fontSize: '11px',
    padding: '3px 8px',
    borderRadius: '4px',
    fontWeight: 'bold'
  },
  streamTitleText: {
    fontSize: '16px',
    fontWeight: 'bold',
    margin: 0,
    color: '#111'
  },
  streamUrlText: {
    fontSize: '13px',
    color: '#555',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  matchTeamsBadge: {
    fontSize: '13px',
    color: '#d32f2f',
    fontWeight: 'bold'
  },
  deleteStreamBtn: {
    background: '#ffebee',
    color: '#d32f2f',
    border: '1px solid #ffcdd2',
    padding: '8px 16px',
    borderRadius: '8px',
    cursor: 'pointer',
    fontWeight: 'bold',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px 20px',
    color: '#888',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '10px'
  },
  matchRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '15px 20px',
    borderRadius: '12px',
    gap: '20px',
    flexWrap: 'wrap'
  },
  matchInfoCol: {
    flex: 1,
    minWidth: '220px'
  },
  matchTeamsNames: {
    fontWeight: 'bold',
    fontSize: '15px',
    color: '#222'
  },
  matchDateText: {
    fontSize: '12px',
    color: '#666',
    marginTop: '4px'
  },
  matchStatusCol: {
    minWidth: '150px'
  },
  selectStatus: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    fontSize: '13px',
    outline: 'none',
    width: '100%'
  },
  matchUrlCol: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    flex: 1.5,
    minWidth: '260px'
  },
  matchUrlInput: {
    padding: '8px 12px',
    borderRadius: '8px',
    border: '1px solid #ccc',
    fontSize: '13px',
    outline: 'none',
    width: '100%'
  }
};

export default LiveManagementSection;
